import { useMemo, useRef, useState } from 'react';
import { provenanceOf, stateLabel, unlinkableReason } from './linkFacts';
import { siteColor, siteKey } from './siteGraph';
import {
  dropVerdict,
  filterPages,
  linkNotice,
  linkedAcross,
  pagesForSite,
} from './sitePairing';
import type { DockPair, SegmentFocus, SiteId } from './sitePairing';
import type { DropMode } from './linkFacts';
import type {
  ExternalPlacement,
  ExternalTarget,
  LinkEdge,
  LinkMapPage,
  SiteNode,
} from '../../types';

/**
 * The two sites you picked, side by side, and the drag that links them.
 *
 * WHY THIS EXISTS. The Sites lens could show that trucking sends 12 links to
 * sunscreen and could do nothing about it — the only way to act was the Tree,
 * which groups pages by SOURCE rather than by site, so building a cross-site
 * link meant finding two pages in one flat list with no site grouping. That is
 * exactly the problem this lens was built to expose.
 *
 * NATIVE HTML5 DRAG, NOT REACT-DND. `DndProvider` is mounted inside
 * `DraggableTree`, and a second one nested inside it throws — see the note at
 * the top of `LinkTree`. The Tree is unmounted in this lens so there is nothing
 * to nest under today, but a plain list needs no provider either way, and not
 * depending on that invariant is cheaper than maintaining it.
 *
 * DIRECTION IS THE TREE'S RULE: the page you drop ON gains the link, pointing
 * at the page you dragged. It is genuinely surprising, so every drop says out
 * loud what it just did.
 */

interface Props {
  pair: DockPair;
  sites: SiteNode[];
  pages: LinkMapPage[];
  edges: LinkEdge[];
  /** True when the page list itself was capped. A column showing 2000 of 5400
   *  pages must not read as the whole site. */
  truncated?: boolean;
  dropMode: DropMode;
  onDropModeChange: (mode: DropMode) => void;
  onClose: (side: 'left' | 'right') => void;
  /** `ownerId` GAINS the link and points at `targetId`. */
  onLink: (ownerId: number, targetId: number, notice: string) => Promise<void>;
  onUnlink: (edge: LinkEdge) => Promise<void>;
  onToggleLock: (edge: LinkEdge) => Promise<void>;
  onOpenPage?: (pageId: number) => void;
  /** Set when an arrow's count was clicked: narrow each column to the pages at
   *  that end of it, so "which twelve of these 61" has an answer. */
  focus?: SegmentFocus | null;
  focusLabel?: string;
  onClearFocus?: () => void;
  /** Off-network addresses, draggable onto any page in either column. */
  targets: ExternalTarget[];
  external: ExternalPlacement[];
  onPlace: (externalTargetId: number, pageId: number) => Promise<void>;
  onUnplace: (placement: ExternalPlacement) => Promise<void>;
}

export default function SitePageDock({
  pair,
  sites,
  pages,
  edges,
  truncated,
  dropMode,
  onDropModeChange,
  onClose,
  onLink,
  onUnlink,
  onToggleLock,
  onOpenPage,
  focus,
  focusLabel,
  onClearFocus,
  targets,
  external,
  onPlace,
  onUnplace,
}: Props) {
  /**
   * POINTER EVENTS, NOT HTML5 DRAG-AND-DROP. Third implementation, and the
   * reason is that the first two were both correct and both did nothing.
   *
   *   1. Gated `preventDefault` on React state, which had not rendered yet when
   *      `dragover` fired, so the row was never a drop target.
   *   2. Gated it on `dataTransfer.types` carrying a custom MIME type. The spec
   *      allows that; Safari does not honour it, so the guard failed the same
   *      way — and a jsdom mock returned the type faithfully, so every test
   *      passed while the browser did nothing.
   *
   * HTML5 DnD hands control to the browser at the exact moment that matters and
   * each engine has its own rules about what may start a drag, what may receive
   * one, and what the transfer object holds mid-flight. Pointer events hand
   * nothing over: a press, some movement, a release, and the element under the
   * cursor. Same in every browser, works with touch, and testable — which is
   * how this one is actually verified rather than assumed.
   */
  const [dragged, setDragged] = useState<number | null>(null);
  /** Where a press began, before it has earned the name "drag". */
  const startRef = useRef<{
    /** A page being linked, or an off-network address being placed. Both are
     *  dropped onto a page row and mean different things there. */
    kind: 'page' | 'external';
    id: number;
    x: number;
    y: number;
  } | null>(null);
  /** Follows the pointer so there is something to see mid-drag. */
  const [ghost, setGhost] = useState<{ x: number; y: number; title: string } | null>(
    null,
  );
  const [over, setOver] = useState<number | null>(null);
  const [notice, setNotice] = useState('');
  /**
   * ONE FILTER PER COLUMN, not one for the dock.
   *
   * A shared box narrowed both sides to the same term, which is exactly wrong
   * for the only thing this surface does: you link a page about one subject to
   * a page about another, so the two columns are almost never looking for the
   * same word. Filtering to "grips" left both sides showing grips pages and
   * nothing to link them to.
   */
  const [queries, setQueries] = useState({ left: '', right: '' });
  /**
   * SHOWN BY DEFAULT.
   *
   * An unwritten page is inert to the AI, which is why the old tree hid them.
   * But this is a place to PLAN, and linking a page you are about to write is a
   * normal thing to want — so they are here, flagged, and hideable when the
   * noise gets in the way.
   */
  const [showUnwritten, setShowUnwritten] = useState(true);
  /** Page ids whose link rows are collapsed. Absent = expanded, so a newly
   *  linked page shows its link without being opted in. */
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);

  const left = useMemo(
    () => (pair.left === undefined ? [] : pagesForSite(pages, pair.left)),
    [pages, pair.left],
  );
  const right = useMemo(
    () => (pair.right === undefined ? [] : pagesForSite(pages, pair.right)),
    [pages, pair.right],
  );

  // MARKED FROM THE FULL LISTS, not the filtered ones. "Already linked with the
  // other side" is a fact about the map, and recomputing it against a search
  // would un-mark a page the moment its partner stopped matching — turning the
  // marker into a statement about the query instead.
  const leftMarked = useMemo(
    () => linkedAcross(edges, left, right),
    [edges, left, right],
  );
  const rightMarked = useMemo(
    () => linkedAcross(edges, right, left),
    [edges, right, left],
  );

  // Focus first, then the typed search — so a search inside a focused segment
  // narrows what the arrow selected rather than escaping it.
  const narrow = (
    list: LinkMapPage[],
    within: Set<number> | undefined,
    q: string,
  ) => {
    const scoped = within ? list.filter((p) => within.has(p.id)) : list;
    const written = showUnwritten ? scoped : scoped.filter((p) => p.is_written);
    return filterPages(written, q);
  };

  const leftShown = useMemo(
    () => narrow(left, focus?.sources, queries.left),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [left, queries.left, focus, showUnwritten],
  );
  const rightShown = useMemo(
    () => narrow(right, focus?.targets, queries.right),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [right, queries.right, focus, showUnwritten],
  );
  const unwrittenCount =
    left.filter((p) => !p.is_written).length +
    right.filter((p) => !p.is_written).length;

  const byId = useMemo(() => {
    const m = new Map<number, LinkMapPage>();
    for (const p of pages) m.set(p.id, p);
    return m;
  }, [pages]);

  /**
   * A page's outgoing links, ready to render UNDER it.
   *
   * A lesson learned the hard way: without this, the entire result of a
   * drop is a number going up. You can see that a link exists and not what it
   * points at, so the artifact being built is invisible on the surface that
   * builds it.
   */
  const placedOn = useMemo(() => {
    const m = new Map<number, ExternalPlacement[]>();
    for (const x of external) {
      const list = m.get(x.page_id);
      if (list) list.push(x);
      else m.set(x.page_id, [x]);
    }
    return m;
  }, [external]);

  const outgoing = useMemo(() => {
    const m = new Map<number, LinkEdge[]>();
    for (const e of edges) {
      const list = m.get(e.page_id);
      if (list) list.push(e);
      else m.set(e.page_id, [e]);
    }
    for (const list of Array.from(m.values())) {
      list.sort((a, b) => a.position - b.position || a.id - b.id);
    }
    return m;
  }, [edges]);

  const DRAG_SLOP = 4;

  /** The page row under the pointer, or null. Read off the EVENT TARGET rather
   *  than `document.elementFromPoint` — same answer, and it works in jsdom, so
   *  the gesture can actually be tested. */
  const rowUnder = (target: EventTarget | null): number | null => {
    const el = (target as Element | null)?.closest?.('[data-page-id]');
    const id = el?.getAttribute('data-page-id');
    return id ? Number(id) : null;
  };

  const endDrag = () => {
    startRef.current = null;
    setDragged(null);
    setGhost(null);
    setOver(null);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const start = startRef.current;
    if (!start) return;

    if (dragged === null) {
      // A press is not a drag until it travels. Below this, a click on a row
      // still behaves like a click.
      if (Math.hypot(e.clientX - start.x, e.clientY - start.y) <= DRAG_SLOP) return;
      setDragged(start.id);
    }
    const title =
      start.kind === 'page'
        ? byId.get(start.id)?.title ?? ''
        : targets.find((t) => t.id === start.id)?.title ??
          targets.find((t) => t.id === start.id)?.url ??
          '';
    setGhost({ x: e.clientX, y: e.clientY, title });

    const under = rowUnder(e.target);
    // An address may land on ANY page; a page may not land on itself.
    setOver(
      under !== null && (start.kind === 'external' || under !== start.id)
        ? under
        : null,
    );
  };

  const onPointerUp = (e: React.PointerEvent) => {
    const start = startRef.current;
    const wasDragging = dragged !== null;
    const under = rowUnder(e.target);
    endDrag();
    if (!start || !wasDragging || under === null) return;
    const droppedOn = byId.get(under);
    if (!droppedOn) return;
    if (start.kind === 'external') {
      handlePlace(droppedOn, start.id);
      return;
    }
    if (under === start.id) return;
    handleDrop(droppedOn, start.id);
  };

  /** Pages with something under them — what Expand all / Collapse act on. */
  const linkedPageIds = useMemo(
    () =>
      [...left, ...right]
        .filter(
          (p) =>
            (outgoing.get(p.id) ?? []).length || (placedOn.get(p.id) ?? []).length,
        )
        .map((p) => p.id),
    [left, right, outgoing, placedOn],
  );

  const handlePlace = async (droppedOn: LinkMapPage, targetId: number) => {
    if (busy) return;
    const already = external.some(
      (x) => x.page_id === droppedOn.id && x.external_target_id === targetId,
    );
    if (already) {
      setNotice(`“${droppedOn.title}” already has that link.`);
      return;
    }
    setBusy(true);
    try {
      await onPlace(targetId, droppedOn.id);
      const t = targets.find((x) => x.id === targetId);
      setNotice(`“${droppedOn.title}” now links to ${t?.title || t?.url}.`);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  const setError = (e: unknown) =>
    setNotice(e instanceof Error ? e.message : 'Could not do that.');

  const handleDrop = async (droppedOn: LinkMapPage, draggedId: number) => {
    setOver(null);
    if (busy) return;
    const from = byId.get(draggedId);
    setDragged(null);

    const verdict = dropVerdict(from, droppedOn, edges);
    if (!verdict.ok) {
      setNotice(verdict.reason);
      return;
    }
    setBusy(true);
    try {
      await onLink(
        droppedOn.id,
        from!.id,
        linkNotice(from!, droppedOn, dropMode === 'both'),
      );
      setNotice(linkNotice(from!, droppedOn, dropMode === 'both'));
    } catch (e) {
      setNotice(e instanceof Error ? e.message : 'Could not create that link.');
    } finally {
      setBusy(false);
    }
  };

  const column = (
    side: 'left' | 'right',
    site: SiteId | undefined,
    list: LinkMapPage[],
    /** How many the site has before the search, so a narrowed column says so
     *  rather than looking like a site that lost its pages. */
    full: number,
    marked: Set<number>,
  ) => {
    if (site === undefined) {
      return (
        <div className="flex items-center justify-center p-6 text-center">
          <p className="text-xs text-gray-400 dark:text-gray-500">
            Click a site above to put its pages here.
          </p>
        </div>
      );
    }
    const index = sites.findIndex((s) => siteKey(s.site_id) === siteKey(site));
    const node = sites[index];
    const color = siteColor(Math.max(0, index));

    return (
      <div className="min-h-0 flex flex-col">
        <div className="shrink-0 px-3 py-1.5 flex items-center gap-2 border-b border-gray-200 dark:border-gray-700">
          <span
            className="w-2.5 h-2.5 rounded-sm shrink-0"
            style={{ backgroundColor: color }}
          />
          <span className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate">
            {node?.name ?? 'Unknown site'}
          </span>
          <span className="text-[11px] text-gray-400 dark:text-gray-500 shrink-0">
            {full === list.length ? list.length : `${list.length} of ${full}`}
          </span>
          <button
            onClick={() => onClose(side)}
            className="icon-btn ml-auto shrink-0 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            aria-label={`Close ${node?.name ?? 'this site'}`}
          >
            ×
          </button>
        </div>

        <div className="shrink-0 px-2 py-1.5 border-b border-gray-100 dark:border-gray-800">
          {/* The × lives INSIDE the field, so clearing is where the text is
              rather than a second control sharing the row's width. */}
          <div className="relative">
            <input
              type="text"
              value={queries[side]}
              onChange={(e) =>
                setQueries((q) => ({ ...q, [side]: e.target.value }))
              }
              placeholder={`Find in ${node?.name ?? 'this site'}…`}
              aria-label={`Find a page in ${node?.name ?? 'this site'}`}
              className="w-full px-2 py-1 pr-6 text-xs border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            {queries[side] && (
              <button
                onClick={() => setQueries((q) => ({ ...q, [side]: '' }))}
                className="icon-btn absolute right-1 top-1/2 -translate-y-1/2 px-1 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                aria-label={`Clear the search in ${node?.name ?? 'this site'}`}
                title="Clear"
              >
                ×
              </button>
            )}
          </div>
        </div>

        <ul className="flex-1 min-h-0 overflow-y-auto">
          {list.length === 0 ? (
            <li className="px-3 py-3 text-xs text-gray-400 dark:text-gray-500">
              {full === 0 ? 'No pages on this site yet.' : 'No pages match.'}
            </li>
          ) : (
            list.map((p) => {
              const state = stateLabel(p);
              const blocked = unlinkableReason(p);
              const rows = outgoing.get(p.id) ?? [];
              const places = placedOn.get(p.id) ?? [];
              const hasRows = rows.length + places.length > 0;
              const shut = collapsed.has(p.id);
              const isOver = over === p.id;
              const isDragged = dragged === p.id;
              return (
                <li
                  key={p.id}
                  data-page-id={p.id}
                  onPointerDown={(e) => {
                    // Only the primary button, and never from a control.
                    if (e.button !== 0) return;
                    if ((e.target as Element).closest('button')) return;
                    startRef.current = {
                      kind: 'page',
                      id: p.id,
                      x: e.clientX,
                      y: e.clientY,
                    };
                  }}
                  className={`px-3 py-1.5 border-b border-gray-100 dark:border-gray-800 last:border-0 cursor-grab active:cursor-grabbing ${
                    isOver
                      ? 'bg-blue-50 dark:bg-blue-900/40 ring-1 ring-inset ring-blue-400'
                      : isDragged
                        ? 'opacity-40'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  }`}
                >
                  {/* SPANS, NOT BUTTONS. Form controls are not draggable, and
                      a `<button>` inside a draggable row swallows the gesture
                      rather than passing it up — with the title filling the
                      row, almost nowhere on it started a drag. That was the
                      whole bug: the drop never fired because dragstart never
                      did. Opening a page moved to its own control on the
                      right. */}
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-gray-300 dark:text-gray-600 shrink-0 text-[10px] leading-none">
                      ⠿
                    </span>
                    {hasRows && (
                      <button
                        onClick={() =>
                          setCollapsed((c) => {
                            const next = new Set(c);
                            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                            next.has(p.id) ? next.delete(p.id) : next.add(p.id);
                            return next;
                          })
                        }
                        draggable={false}
                        className="icon-btn shrink-0 w-3 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                        aria-label={`${shut ? 'Show' : 'Hide'} the links from ${p.title}`}
                        aria-expanded={!shut}
                      >
                        {shut ? '▸' : '▾'}
                      </button>
                    )}
                    <span
                      className="min-w-0 flex-1 text-xs text-gray-700 dark:text-gray-200 truncate"
                      // The reason a link to this page will not ship, on the
                      // page it is about. `stateLabel` beside it is the two-word
                      // version; this is the sentence.
                      title={blocked ? `${p.title} - ${blocked}` : p.title}
                    >
                      {p.title}
                    </span>
                    {marked.has(p.id) && (
                      // The marker is what makes this a work surface instead of
                      // a picker: without it, "have I done this one" can only be
                      // answered by auditing the tree.
                      <span
                        className="shrink-0 text-[10px] px-1 rounded"
                        style={{ backgroundColor: `${color}22`, color }}
                        title="Already linked with the other side"
                      >
                        linked
                      </span>
                    )}
                    {state && (
                      <span className="shrink-0 text-[10px] text-amber-700 dark:text-amber-400">
                        {state}
                      </span>
                    )}
                    <button
                      onClick={() => onOpenPage?.(p.id)}
                      draggable={false}
                      className="shrink-0 text-[10px] text-gray-400 hover:text-blue-700 dark:hover:text-blue-300"
                      title="Open this page"
                      aria-label={`Open ${p.title}`}
                    >
                      ↗
                    </button>
                  </div>

                  {/* WHAT THIS PAGE POINTS AT, right under it. The tree learned
                      this the hard way: with links only as a count, the whole
                      visible result of a drop is a number going up, and the
                      thing you are building is invisible on the screen that
                      builds it. */}
                  {!shut &&
                    places.map((placement) => (
                      <div
                        key={`x${placement.id}`}
                        draggable={false}
                        className="ml-4 mt-0.5 flex items-baseline gap-1.5 group"
                      >
                        <span className="shrink-0 text-[10px] text-gray-400 dark:text-gray-500">
                          ↗
                        </span>
                        <span
                          className="min-w-0 flex-1 text-[11px] text-gray-500 dark:text-gray-400 truncate"
                          title={placement.url}
                        >
                          {placement.title || placement.url}
                        </span>
                        <span className="shrink-0 text-[10px] px-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                          {placement.rel.toLowerCase()}
                        </span>
                        <button
                          onClick={() => onUnplace(placement)}
                          draggable={false}
                          className="icon-btn shrink-0 text-gray-300 dark:text-gray-600 hover:text-red-600 dark:hover:text-red-400 opacity-0 group-hover:opacity-100"
                          title="Take this off the page"
                          aria-label={`Remove the external link on ${p.title}`}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  {!shut &&
                    rows.map((edge) => (
                    <div
                      key={edge.id}
                      draggable={false}
                      className="ml-4 mt-0.5 flex items-baseline gap-1.5 group"
                    >
                      <span className="shrink-0 text-[10px] text-gray-400 dark:text-gray-500">
                        {edge.reciprocal ? '↔' : '→'}
                      </span>
                      <span
                        className="min-w-0 flex-1 text-[11px] text-gray-500 dark:text-gray-400 truncate"
                        title={
                          edge.anchor_text
                            ? `“${edge.anchor_text}” → ${edge.target_title}`
                            : edge.target_title
                        }
                      >
                        {edge.target_title}
                      </span>
                      {/* WHERE IT CAME FROM, and whether Relink will keep it.
                          Without this, Relink is a button nobody dares press:
                          "will this delete the afternoon I just spent" has no
                          visible answer. */}
                      {(() => {
                        const prov = provenanceOf(edge);
                        return (
                          <span
                            className={`shrink-0 text-[10px] px-1 rounded ${prov.className}`}
                            title={
                              edge.reason ? `${prov.title}\n\n${edge.reason}` : prov.title
                            }
                          >
                            {prov.label}
                          </span>
                        );
                      })()}
                      {edge.target_is_published ? (
                        // THE RESOLVED HREF — the address that will actually be
                        // emitted. Showing it is the whole defence against the
                        // bug this feature exists to fix: a link that looks
                        // right in the editor and 404s on the site.
                        edge.target_href && (
                          <span
                            className="shrink-0 max-w-[40%] truncate text-[10px] font-mono text-gray-400 dark:text-gray-500"
                            title={edge.target_href}
                          >
                            {edge.target_href}
                          </span>
                        )
                      ) : (
                        // The deployer DROPS an edge whose target has never
                        // been public. Saying so here is the difference between
                        // a link that will ship and one that silently will not.
                        <span
                          className="shrink-0 text-[10px] text-red-600 dark:text-red-400"
                          title="This target has never been deployed, so the link is skipped at publish."
                        >
                          not live
                        </span>
                      )}
                      <button
                        onClick={() => onToggleLock(edge)}
                        draggable={false}
                        className={`shrink-0 text-[10px] ${
                          edge.locked
                            ? 'text-amber-600 dark:text-amber-400'
                            : 'text-gray-300 dark:text-gray-600 opacity-0 group-hover:opacity-100'
                        }`}
                        title={
                          edge.locked
                            ? 'Locked - Relink will not replace this'
                            : 'Lock so Relink cannot replace this'
                        }
                      >
                        {edge.locked ? '🔒' : '🔓'}
                      </button>
                      <button
                        onClick={() => onUnlink(edge)}
                        draggable={false}
                        className="icon-btn shrink-0 text-gray-300 dark:text-gray-600 hover:text-red-600 dark:hover:text-red-400 opacity-0 group-hover:opacity-100"
                        title="Remove this link"
                        aria-label={`Remove the link to ${edge.target_title}`}
                      >
                        ×
                      </button>
                    </div>
                    ))}
                </li>
              );
            })
          )}
        </ul>
      </div>
    );
  };

  return (
    <div
      className="flex-1 min-h-0 flex flex-col select-none"
      // On the CONTAINER, not the rows: once the pointer leaves the row it
      // started on, only the container still sees every move. `closest` on the
      // event target then says which row it is over.
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={endDrag}
      onPointerLeave={endDrag}
    >
      {ghost && (
        // Something to look at while dragging. Without it the gesture has no
        // feedback at all until the pointer happens to cross a valid row.
        <div
          className="fixed z-[80] pointer-events-none px-2 py-1 rounded shadow-lg bg-white dark:bg-gray-900 border border-blue-400 text-xs text-gray-700 dark:text-gray-200 max-w-[16rem] truncate"
          style={{ left: ghost.x + 12, top: ghost.y + 12 }}
        >
          {ghost.title}
        </div>
      )}
      <div className="shrink-0 px-3 py-1.5 flex flex-wrap items-center gap-2 bg-gray-50 dark:bg-gray-900/40">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Drag a page onto another - the page you drop on links to the one you
          dragged.
        </p>

        {/* The Tree's control, rendered again here. It lives in the Tree's
            toolbar, which is unmounted in this lens — without it, two-way
            linking is simply unreachable from this screen. Same state, so the
            two surfaces cannot disagree. */}
        <div
          role="radiogroup"
          aria-label="What a drop creates"
          className="inline-flex p-0.5 rounded-md bg-gray-200/70 dark:bg-gray-700/70"
        >
          {(
            [
              ['one-way', 'One way'],
              ['both', 'Both ways'],
            ] as [DropMode, string][]
          ).map(([mode, label]) => (
            <button
              key={mode}
              role="radio"
              aria-checked={dropMode === mode}
              onClick={() => onDropModeChange(mode)}
              className={`px-2 py-0.5 text-[11px] font-medium rounded ${
                dropMode === mode
                  ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {unwrittenCount > 0 && (
          <label className="flex items-center gap-1 text-[11px] text-gray-500 dark:text-gray-400 cursor-pointer">
            <input
              type="checkbox"
              checked={!showUnwritten}
              onChange={(e) => setShowUnwritten(!e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600"
            />
            Hide {unwrittenCount} unwritten
          </label>
        )}

        {linkedPageIds.length > 0 && (
          <div className="inline-flex rounded border border-gray-300 dark:border-gray-600 overflow-hidden">
            <button
              onClick={() => setCollapsed(new Set())}
              className="px-1.5 py-0.5 text-[11px] text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              title="Show every link under every page"
            >
              Expand all
            </button>
            <button
              onClick={() => setCollapsed(new Set(linkedPageIds))}
              className="px-1.5 py-0.5 text-[11px] text-gray-600 dark:text-gray-300 border-l border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"
              title="Collapse back to the pages"
            >
              Collapse
            </button>
          </div>
        )}

        {notice && (
          <span className="ml-auto text-xs text-blue-700 dark:text-blue-300 truncate max-w-[50%]">
            {notice}
          </span>
        )}
      </div>

      {targets.length > 0 && (
        // THE OFF-NETWORK ADDRESSES, above both columns rather than inside one.
        // They are not a site's pages and belong to neither side — and you want
        // to drop one onto whichever column happens to hold the page, so a
        // strip that spans both is the only place it can live.
        <div className="shrink-0 px-3 py-1.5 flex flex-wrap items-center gap-1.5 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
          <span
            className="text-[11px] text-gray-500 dark:text-gray-400 shrink-0"
            title="Off-network addresses you added under External links. Drop one on a page to link out to it."
          >
            <span className="font-medium text-gray-600 dark:text-gray-300">
              External links
            </span>
            {' - drag onto a page:'}
          </span>
          {targets.map((t) => (
            <span
              key={t.id}
              onPointerDown={(e) => {
                if (e.button !== 0) return;
                startRef.current = {
                  kind: 'external',
                  id: t.id,
                  x: e.clientX,
                  y: e.clientY,
                };
              }}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-[11px] text-gray-700 dark:text-gray-200 cursor-grab active:cursor-grabbing"
              title={`${t.url} · ${t.rel.toLowerCase()} · on ${t.placement_count} page${
                t.placement_count === 1 ? '' : 's'
              }`}
            >
              <span className="text-gray-400">↗</span>
              <span className="max-w-[10rem] truncate">{t.title || t.url}</span>
              {t.placement_count > 0 && (
                <span className="text-gray-400 tabular-nums">
                  {t.placement_count}
                </span>
              )}
            </span>
          ))}
        </div>
      )}

      {focus && (
        <div className="shrink-0 px-3 py-1 text-[11px] flex items-center gap-2 bg-blue-50 dark:bg-blue-900/25 text-blue-800 dark:text-blue-200">
          <span className="truncate">
            Showing only the pages in {focusLabel ?? 'this link'}.
          </span>
          <button
            onClick={onClearFocus}
            className="ml-auto shrink-0 underline hover:no-underline"
          >
            Show all pages
          </button>
        </div>
      )}

      {truncated && (
        // NEVER a silent cut. A column that stops at the cap reads as the whole
        // site, and someone plans coverage from it.
        <p className="shrink-0 px-3 py-1 text-[11px] text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20">
          This map has more pages than the list can hold, so these columns are
          incomplete.
        </p>
      )}

      {/* `flex-1 min-h-0`, not a fixed height: the dock is a resizable pane now,
          so the columns take whatever the handle above them left. */}
      <div className="grid grid-cols-2 divide-x divide-gray-200 dark:divide-gray-700 flex-1 min-h-0">
        {column('left', pair.left, leftShown, left.length, leftMarked)}
        {column('right', pair.right, rightShown, right.length, rightMarked)}
      </div>
    </div>
  );
}
